import type { MarketSelectorPreset } from '@src/components/MarketSelector/types';
import { OrderPresetSearchSelect } from '@src/components/OrderPresets/OrderPresetSearchSelect';
import { SubAccountSearchSelect } from '@src/components/SubAccounts/SubAccountSearchSelect';
import type { IOrderPreset } from '@src/providers/OrderPresetsContext';
import { Box, FormControlSizes, Grid, HStack, type ISubaccount } from '@talos/kyoko';
import { MarketCardMarketAccountButton, MarketCardMarketAccountButtonViewMode } from '../MarketCardMarketAccountButton';

const CONTROL_ROW_SLOT = {
  Preset: 'preset',
  MarketSelector: 'marketSelector',
  SubAccount: 'subAccount',
} as const;
type ControlRowSlot = (typeof CONTROL_ROW_SLOT)[keyof typeof CONTROL_ROW_SLOT];

interface MarketDataCardControlRowProps {
  showMarketSelectorNavigation?: boolean;
  isEditSymbolMode?: boolean;
  showOrderPresets: boolean;
  showSubAccountSelector: boolean;
  onMarketSelectorClick?: () => void;
  aggregationPresets: MarketSelectorPreset[];
  selectedAggregationID?: string;
  onChangeAggregation: (aggregationID: string) => void;
  marketSelectorButtonLabel: string;
  selectedMarketOrAccountNames: string[];
  orderPresetSelection: IOrderPreset | undefined;
  onChangeOrderPreset: (preset?: IOrderPreset) => void;
  onToggleFavoritePreset: (orderPreset: IOrderPreset) => void;
  orderPresetOptions: IOrderPreset[];
  subAccountSelection: ISubaccount | undefined;
  subAccountOptions: ISubaccount[];
  onSubAccountChange: (subAccount?: ISubaccount) => void;
}

export function MarketDataCardControlRow({
  showMarketSelectorNavigation = true,
  isEditSymbolMode = false,
  showOrderPresets,
  showSubAccountSelector,
  onMarketSelectorClick,
  aggregationPresets,
  selectedAggregationID,
  onChangeAggregation,
  marketSelectorButtonLabel,
  selectedMarketOrAccountNames,
  orderPresetSelection,
  onChangeOrderPreset,
  onToggleFavoritePreset,
  orderPresetOptions,
  subAccountSelection,
  subAccountOptions,
  onSubAccountChange,
}: MarketDataCardControlRowProps) {
  const controlRowLayout = getControlRowLayout({
    showOrderPresets,
    showSubAccountSelector,
    showMarketSelectorButton: onMarketSelectorClick != null,
  });

  if (!controlRowLayout.slots.length) {
    return null;
  }

  return (
    <Grid gridTemplateColumns={controlRowLayout.gridTemplateColumns} gap="spacing4">
      {controlRowLayout.slots.map(slot => {
        if (slot === CONTROL_ROW_SLOT.Preset) {
          return (
            <Box key={CONTROL_ROW_SLOT.Preset}>
              <OrderPresetSearchSelect
                selection={orderPresetSelection}
                onChange={onChangeOrderPreset}
                size={FormControlSizes.Tiny}
                showNavigation
                centered
                showChevron={false}
                options={orderPresetOptions}
                id="market-card-preset-selector"
                onToggleFavorite={onToggleFavoritePreset}
              />
            </Box>
          );
        }

        if (slot === CONTROL_ROW_SLOT.MarketSelector && onMarketSelectorClick) {
          return (
            <HStack key={CONTROL_ROW_SLOT.MarketSelector} w="100%">
              <Box flex="1">
                <MarketCardMarketAccountButton
                  onClick={onMarketSelectorClick}
                  size={FormControlSizes.Tiny}
                  viewMode={controlRowLayout.marketSelectorButtonViewMode}
                  showAggregationNavigation={showMarketSelectorNavigation}
                  isEditSymbolMode={isEditSymbolMode}
                  label={marketSelectorButtonLabel}
                  selectedMarketOrAccountNames={selectedMarketOrAccountNames}
                  selectedAggregationID={selectedAggregationID}
                  aggregationPresets={aggregationPresets}
                  onChangeAggregation={onChangeAggregation}
                />
              </Box>
            </HStack>
          );
        }

        if (slot === CONTROL_ROW_SLOT.SubAccount) {
          return (
            <Box key={CONTROL_ROW_SLOT.SubAccount}>
              <SubAccountSearchSelect
                selection={subAccountSelection}
                size={FormControlSizes.Tiny}
                showNavigation
                centered
                showChevron={false}
                options={subAccountOptions}
                onChange={onSubAccountChange}
                data-testid="sub-account-selector"
              />
            </Box>
          );
        }

        return null;
      })}
    </Grid>
  );
}

function getControlRowLayout({
  showOrderPresets,
  showSubAccountSelector,
  showMarketSelectorButton,
}: {
  showOrderPresets: boolean;
  showSubAccountSelector: boolean;
  showMarketSelectorButton: boolean;
}) {
  const slots: ControlRowSlot[] = [];

  if (showOrderPresets) {
    slots.push(CONTROL_ROW_SLOT.Preset);
  }

  if (showMarketSelectorButton) {
    slots.push(CONTROL_ROW_SLOT.MarketSelector);
  }

  if (showSubAccountSelector) {
    slots.push(CONTROL_ROW_SLOT.SubAccount);
  }

  return {
    slots,
    gridTemplateColumns: getGridTemplateColumns(slots.length),
    marketSelectorButtonViewMode:
      slots.length === 3 ? MarketCardMarketAccountButtonViewMode.Icon : MarketCardMarketAccountButtonViewMode.FullWidth,
  };
}

function getGridTemplateColumns(slotCount: number) {
  if (slotCount === 1) {
    return '1fr';
  }

  if (slotCount === 2) {
    return 'repeat(2, minmax(0, 1fr))';
  }

  if (slotCount === 3) {
    return '1fr auto 1fr';
  }

  return '1fr';
}
